import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router-dom"
import AttachDetail from "customerAttachments/components/AttachmentDetail"
import { IdCards, PurchaseAttachMapping } from "customerAttachments/constants"
import { useModal } from "customerAttachments/contexts/ModalProvider"
import { PurchaseAttachType } from "customerAttachments/types"
import { handleErrorMessageCode } from "customerAttachments/utils/apiUtil"
import { MESSAGE_CODE_NO_DELIVERY_DATE_CANT_UPLOAD_ATTACH } from "modules/messageCodeSales"
import { useNavigation } from "modules/navigation"

import useGetPurchaseAttachment from "./hooks/useGetPurchaseAttachment"
import useUploadPurchaseAttachments from "./hooks/useUploadPurchaseAttachment"

type UploadFile = {
  FileName: string
  Base64: string
}

const PurchaseAttachDetail = () => {
  const { orderNo = "" } = useParams<{ orderNo: string }>()
  const [searchParams] = useSearchParams()
  const attachType = searchParams.get("type") as PurchaseAttachType
  const actionStatus = searchParams.get("action")
  const { data: attachInfo = {}, isLoading, isSuccess } = useGetPurchaseAttachment({ orderNo, attachType })
  const { mutate, isLoading: isUploading } = useUploadPurchaseAttachments({ orderNo, attachType })
  const { navigateTo } = useNavigation()
  const { openModal } = useModal()
  const { t } = useTranslation(["sales", "label"])
  const isIdCard = attachType && IdCards.includes(attachType)
  const attachTypeText = (attachType && PurchaseAttachMapping[attachType]?.Text) || ""
  const [files, setFiles] = useState<Array<UploadFile | null>>(Array(isIdCard ? 2 : 1).fill(null))

  useEffect(() => {
    if (!actionStatus && isSuccess) {
      searchParams.set("action", attachInfo?.FileUrl ? "preview" : "edit")
      navigateTo(`/purchase/${orderNo}/attachment?${searchParams.toString()}`)
    }
  }, [attachInfo?.FileUrl, actionStatus, isSuccess, orderNo, navigateTo, searchParams])

  const goBack = () => navigateTo(`/purchase/${orderNo}`)

  const handleSubmit = async (files: UploadFile[]) => {
    try {
      await mutate({ Files: files })
      setTimeout(() => {
        openModal({
          title: t("order.checklist_upload_success"),
          content: t("customer_attachments.upload_success"),
          showCancel: false,
          onConfirm: goBack,
          onCancel: goBack,
        })
      }, 300)
    } catch (error: unknown) {
      const messageCode = handleErrorMessageCode(error)
      openModal({
        title: t("order.checklist_msg_upload_fail"),
        content:
          messageCode === String(MESSAGE_CODE_NO_DELIVERY_DATE_CANT_UPLOAD_ATTACH)
            ? t("customer_attachments.upload_failed.no_delivery_date")
            : t("customer_attachments.upload_failed"),
        showCancel: false,
        confirmButtonText: t("label:okay"),
      })
    }
  }

  return (
    <AttachDetail
      attachType={attachType}
      attachTypeText={attachTypeText}
      goBack={goBack}
      url={attachInfo?.FileUrl}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      setFiles={setFiles}
      files={files}
      isUploading={isUploading}
    />
  )
}

export default PurchaseAttachDetail
